import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_STATUS_MOVIMENTACAO,
  ROTULO_TIPO_MOVIMENTACAO,
  STATUS_MOVIMENTACAO,
  TIPOS_MOVIMENTACAO,
  type SituacaoPrazo,
  type StatusMovimentacao,
  type TipoMovimentacao,
} from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { CampoSelecao, CampoTexto, CampoTextoLongo } from '../componentes/Campo.tsx';
import { Dialogo } from '../componentes/Dialogo.tsx';
import { DocumentosDoContato } from '../componentes/DocumentosDoContato.tsx';
import { RecusaDaApi } from '../componentes/RecusaDaApi.tsx';
import { CabecalhoDePagina, Botao, Cartao, Celula, Indicador, Linha, Pilula, Tabela, data, type Tom, } from '../componentes/Superficie.tsx';
import { comValorAtual } from '../dominio/catalogo.ts';
import { useOperadoras } from '../dominio/operadoras.ts';
import { dia, ler } from '../formulario.ts';

type Movimentacao = {
  id: string;
  contato: { id: string; nome: string };
  beneficiario: { id: string; nome: string } | null;
  tipo: TipoMovimentacao;
  status: StatusMovimentacao;
  operadora: string | null;
  protocolo: string | null;
  abertaEm: string;
  prazo: string | null;
  situacaoPrazo: SituacaoPrazo;
  diasDePrazo: number;
  observacoes: string | null;
  responsavel: { id: string; nome: string };
  documentos: number;
};

type Beneficiario = { id: string; nome: string };

type Cliente = { contatoId: string; nome: string };

const FILTROS = [
  { valor: 'ABERTAS', rotulo: 'Abertas' },
  { valor: 'ATRASADAS', rotulo: 'Atrasadas' },
  { valor: 'TODAS', rotulo: 'Todas' },
] as const;

const TOM_DO_PRAZO: Record<SituacaoPrazo, Tom> = {
  SEM_PRAZO: 'neutro',
  NO_PRAZO: 'sucesso',
  PROXIMO: 'alerta',
  ATRASADO: 'erro',
};

/** A régua de cor do protótipo: concluída é verde, cancelada é vermelha, e o
  * que ainda depende de terceiro fica neutro em vez de alarmar sem motivo. */
const TOM_DO_STATUS_DA_MOVIMENTACAO: Record<StatusMovimentacao, Tom> = {
  ABERTA: 'neutro',
  EM_ANDAMENTO: 'alerta',
  AGUARDANDO_CLIENTE: 'neutro',
  AGUARDANDO_OPERADORA: 'neutro',
  CONCLUIDA: 'sucesso',
  CANCELADA: 'erro',
};

/**
 * Movimentações de contrato ativo.
 *
 * O par que torna o pedido cobrável é protocolo + prazo: sem protocolo não há
 * o que cobrar da operadora, e sem prazo não há o que cobrar de nós.
 */
export function PaginaMovimentacoes(): React.JSX.Element {
  const [filtro, definirFiltro] = useState<(typeof FILTROS)[number]['valor']>('ABERTAS');
  const [criando, definirCriando] = useState(false);
  const [editando, definirEditando] = useState<Movimentacao | null>(null);

  const consulta = useQuery({
    queryKey: ['movimentacoes', filtro],
    queryFn: () => chamarApi<Movimentacao[]>(`/movimentacoes?filtro=${filtro}`),
  });

  // Carteira inteira, para os indicadores do topo.
  const consultaDeTodas = useQuery({
    queryKey: ['movimentacoes', 'TODAS'],
    queryFn: () => chamarApi<Movimentacao[]>('/movimentacoes?filtro=TODAS'),
  });

  const movimentacoes = consulta.data ?? [];
  const todas = consultaDeTodas.data ?? [];
  const porStatus = (status: StatusMovimentacao): number =>
    todas.filter((linha) => linha.status === status).length;

  return (
    <section>
      <CabecalhoDePagina
        area="Operação"
        titulo="Movimentações"
        descricao="Inclusões, exclusões e alterações com documentos, protocolo, prazo e histórico."
        acoes={
          <Botao variante="primario" onClick={() => definirCriando(true)}>
            + Nova movimentação
          </Botao>
        }
      />

      {/* Os indicadores falam da carteira inteira, não do filtro escolhido:
        * filtrar a lista não muda quantas movimentações existem. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Indicador rotulo="Abertas" valor={String(porStatus('ABERTA'))} />
        <Indicador
          rotulo="Em andamento"
          valor={String(
            porStatus('EM_ANDAMENTO') +
              porStatus('AGUARDANDO_CLIENTE') +
              porStatus('AGUARDANDO_OPERADORA'),
          )}
        />
        <Indicador rotulo="Concluídas" valor={String(porStatus('CONCLUIDA'))} />
        <Indicador
          rotulo="Documentos"
          valor={String(todas.reduce((total, linha) => total + linha.documentos, 0))}
        />
        <Indicador
          rotulo="SLA vencido"
          valor={String(todas.filter((linha) => linha.situacaoPrazo === 'ATRASADO').length)}
          severidade="erro"
        />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTROS.map((opcao) => (
          <Botao
            key={opcao.valor}
            variante={filtro === opcao.valor ? 'primario' : 'secundario'}
            onClick={() => definirFiltro(opcao.valor)}
          >
            {opcao.rotulo}
          </Botao>
        ))}
      </div>

      <Cartao
        className="mt-3"
        titulo="Movimentações da carteira"
        descricao="Cada registro é conectado ao Cliente 360º e à pasta de documentos."
      >
        <Tabela
          colunas={[
            'Abertura',
            'Cliente',
            'Tipo',
            'Beneficiário',
            'Operadora',
            'Protocolo',
            'Prazo',
            'Status',
            'Docs',
            'Ação',
          ]}
          vazio="Nenhuma movimentação neste filtro."
          carregando={consulta.isLoading}
        >
          {movimentacoes.map((movimentacao) => (
            <Linha key={movimentacao.id}>
              <Celula>{data(movimentacao.abertaEm)}</Celula>
              <Celula className="font-medium">{movimentacao.contato.nome}</Celula>
              <Celula>{ROTULO_TIPO_MOVIMENTACAO[movimentacao.tipo]}</Celula>
              <Celula>{movimentacao.beneficiario?.nome ?? '—'}</Celula>
              <Celula>{movimentacao.operadora ?? '—'}</Celula>
              <Celula>{movimentacao.protocolo ?? '—'}</Celula>
              <Celula>
                <Pilula tom={TOM_DO_PRAZO[movimentacao.situacaoPrazo]}>
                  {movimentacao.situacaoPrazo === 'SEM_PRAZO'
                    ? 'Sem prazo'
                    : movimentacao.situacaoPrazo === 'ATRASADO'
                      ? `${Math.abs(movimentacao.diasDePrazo)}d atrasado`
                      : data(movimentacao.prazo)}
                </Pilula>
              </Celula>
              <Celula>
                <Pilula tom={TOM_DO_STATUS_DA_MOVIMENTACAO[movimentacao.status]}>
                  {ROTULO_STATUS_MOVIMENTACAO[movimentacao.status]}
                </Pilula>
              </Celula>
              <Celula>{movimentacao.documentos}</Celula>
              <Celula>
                <Botao tamanho="mini" onClick={() => definirEditando(movimentacao)}>
                  Editar
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Tabela>
      </Cartao>

      {criando ? <NovaMovimentacao aoFechar={() => definirCriando(false)} /> : null}

      {editando ? (
        <JanelaDaMovimentacao
          movimentacao={editando}
          aoFechar={() => definirEditando(null)}
        />
      ) : null}
    </section>
  );
}

function NovaMovimentacao({ aoFechar }: { aoFechar: () => void }): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [contatoId, definirContatoId] = useState('');
  const [tipo, definirTipo] = useState<TipoMovimentacao>('INCLUSAO');
  const [operadora, definirOperadora] = useState('');
  const [protocolo, definirProtocolo] = useState('');
  const operadoras = useOperadoras();
  const [prazo, definirPrazo] = useState(() => emDias(5));
  const [observacoes, definirObservacoes] = useState('');

  const clientes = useQuery({
    queryKey: ['carteira', ''],
    queryFn: () => chamarApi<Cliente[]>('/contatos/carteira?busca='),
  });

  const criar = useMutation({
    mutationFn: () =>
      chamarApi('/movimentacoes', {
        metodo: 'POST',
        corpo: {
          contatoId,
          tipo,
          operadora: operadora || undefined,
          protocolo: protocolo || undefined,
          prazo,
          observacoes: observacoes || undefined,
        },
      }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['movimentacoes'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['dashboard'] });
      aoFechar();
    },
  });

  function enviar(evento: FormEvent): void {
    evento.preventDefault();
    if (contatoId) criar.mutate();
  }

  return (
    <Dialogo
      titulo="Nova movimentação"
      descricao="O pedido nasce com prazo: é ele que gera a tarefa de acompanhamento."
      aoFechar={aoFechar}
    >
      <form onSubmit={enviar} className="space-y-3">
        <CampoSelecao
          rotulo="Cliente"
          required
          value={contatoId}
          onChange={(evento) => definirContatoId(evento.target.value)}
        >
          <option value="">Selecione</option>
          {(clientes.data ?? []).map((cliente) => (
            <option key={cliente.contatoId} value={cliente.contatoId}>
              {cliente.nome}
            </option>
          ))}
        </CampoSelecao>

        <CampoSelecao
          rotulo="Tipo"
          value={tipo}
          onChange={(evento) => definirTipo(evento.target.value as TipoMovimentacao)}
        >
          {TIPOS_MOVIMENTACAO.map((opcao) => (
            <option key={opcao} value={opcao}>
              {ROTULO_TIPO_MOVIMENTACAO[opcao]}
            </option>
          ))}
        </CampoSelecao>

        <div className="grid gap-3 sm:grid-cols-2">
          <CampoSelecao
            rotulo="Operadora"
            value={operadora}
            onChange={(evento) => definirOperadora(evento.target.value)}
          >
            <option value="">— selecione —</option>
            {operadoras.nomes.map((nome) => (
              <option key={nome} value={nome}>
                {nome}
              </option>
            ))}
          </CampoSelecao>
          <CampoTexto
            rotulo="Protocolo"
            value={protocolo}
            onChange={(evento) => definirProtocolo(evento.target.value)}
          />
        </div>

        <CampoTexto
          rotulo="Prazo"
          type="date"
          value={prazo}
          onChange={(evento) => definirPrazo(evento.target.value)}
        />

        <CampoTextoLongo
          rotulo="Observações"
          rows={3}
          value={observacoes}
          onChange={(evento) => definirObservacoes(evento.target.value)}
        />

        {criar.error ? (
          <p className="text-sm text-erro">
            {criar.error instanceof ErroDaApi
              ? criar.error.message
              : 'Não foi possível criar a movimentação.'}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao tipo="submit" variante="primario" desabilitado={criar.isPending}>
            {criar.isPending ? 'Criando…' : 'Criar movimentação'}
          </Botao>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * A janela "Editar movimentação" do protótipo.
 *
 * O `PATCH /movimentacoes/:id` aceita oito campos e, até aqui, nenhuma tela
 * mandava mais do que o status — quem visse o protocolo errado não tinha por
 * onde arrumar. É o mesmo caso que a Implantação resolveu: contrato pronto,
 * interface faltando.
 *
 * O cliente aparece travado: a movimentação pertence ao Contato, e trocar o
 * dono do pedido não é corrigir um registro — é criar outro.
 */
function JanelaDaMovimentacao({
  movimentacao,
  aoFechar,
}: {
  movimentacao: Movimentacao;
  aoFechar: () => void;
}): React.JSX.Element {
  const operadoras = useOperadoras();
  const { usuaria } = useSessao();
  const clienteDeConsultas = useQueryClient();
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';

  const beneficiarios = useQuery({
    queryKey: ['beneficiarios', movimentacao.contato.id],
    queryFn: () =>
      chamarApi<Beneficiario[]>(`/contatos/${movimentacao.contato.id}/beneficiarios`),
  });

  const usuarias = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<Array<{ id: string; nome: string; ativa: boolean }>>('/usuarias'),
  });
  const responsaveis = (usuarias.data ?? []).filter((uma) => uma.ativa);

  /**
   * Controlados, e não `defaultValue`: as duas listas chegam por consulta,
   * depois da primeira pintura. Num `<select>` não-controlado o navegador
   * assume a primeira opção quando ela aparece — foi assim que a Implantação
   * trocou de responsável sem ninguém pedir.
   *
   * O estado guarda só a escolha de quem preencheu; enquanto ninguém mexeu,
   * ele é nulo e o campo mostra o que está gravado. Copiar o valor da
   * movimentação para dentro do estado faria a janela segurar uma cópia que
   * não acompanha mais a origem.
   */
  const [beneficiarioEscolhido, escolherBeneficiario] = useState<string | null>(null);
  const [responsavelEscolhido, escolherResponsavel] = useState<string | null>(null);

  const beneficiarioId = beneficiarioEscolhido ?? movimentacao.beneficiario?.id ?? '';
  const responsavelId = responsavelEscolhido ?? movimentacao.responsavel.id;

  const salvar = useMutation({
    mutationFn: (campos: FormData) =>
      chamarApi(`/movimentacoes/${movimentacao.id}`, {
        metodo: 'PATCH',
        corpo: {
          tipo: String(campos.get('tipo')) as TipoMovimentacao,
          status: String(campos.get('status')) as StatusMovimentacao,
          operadora: ler(campos, 'operadora'),
          protocolo: ler(campos, 'protocolo'),
          ...prazoInformado(campos),
          observacoes: ler(campos, 'observacoes'),
          beneficiarioId: beneficiarioId || null,
          // Reatribuir é decisão da Administradora; a API ignora o campo vindo
          // de qualquer outro papel.
          ...(ehAdministradora ? { responsavelId } : {}),
        },
      }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['movimentacoes'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['dashboard'] });
      aoFechar();
    },
  });

  return (
    <Dialogo
      area="Operação"
      titulo="Editar movimentação"
      descricao={`${movimentacao.contato.nome} · aberta em ${data(movimentacao.abertaEm)}`}
      aoFechar={aoFechar}
    >
      <form
        data-teste="formulario-movimentacao"
        onSubmit={(evento) => {
          evento.preventDefault();
          salvar.mutate(new FormData(evento.currentTarget));
        }}
      >
        <CampoTexto rotulo="Cliente" value={movimentacao.contato.nome} disabled readOnly />

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <CampoSelecao rotulo="Tipo" name="tipo" defaultValue={movimentacao.tipo}>
            {TIPOS_MOVIMENTACAO.map((opcao) => (
              <option key={opcao} value={opcao}>
                {ROTULO_TIPO_MOVIMENTACAO[opcao]}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao rotulo="Status" name="status" defaultValue={movimentacao.status}>
            {STATUS_MOVIMENTACAO.map((status) => (
              <option key={status} value={status}>
                {ROTULO_STATUS_MOVIMENTACAO[status]}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao
            key={operadoras.pronta ? 'operadoras-prontas' : 'operadoras-carregando'}
            rotulo="Operadora"
            name="operadora"
            defaultValue={movimentacao.operadora ?? ''}
          >
            <option value="">— selecione —</option>
            {comValorAtual(operadoras.nomes, movimentacao.operadora).map((nome) => (
              <option key={nome} value={nome}>
                {nome}
              </option>
            ))}
          </CampoSelecao>
          <CampoTexto
            rotulo="Protocolo"
            name="protocolo"
            maxLength={80}
            defaultValue={movimentacao.protocolo ?? ''}
          />

          {/* Obrigatório só quando já existe prazo gravado: a API aceita
            * abrir a movimentação sem prazo, mas não aceita apagar o que foi
            * posto. Exigir sempre trancaria a janela de um registro sem
            * prazo — justamente o que esta tela veio destravar. */}
          <CampoTexto
            rotulo="Prazo"
            name="prazo"
            type="date"
            required={Boolean(movimentacao.prazo)}
            defaultValue={dia(movimentacao.prazo)}
          />

          <CampoSelecao
            rotulo="Beneficiário"
            value={beneficiarioId}
            onChange={(evento) => escolherBeneficiario(evento.target.value)}
            dica="Deixe em branco quando o pedido é do contrato inteiro."
          >
            <option value="">Nenhum</option>
            {(beneficiarios.data ?? []).map((pessoa) => (
              <option key={pessoa.id} value={pessoa.id}>
                {pessoa.nome}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao
            rotulo="Responsável"
            className="md:col-span-2"
            value={responsavelId}
            disabled={!ehAdministradora}
            onChange={(evento) => escolherResponsavel(evento.target.value)}
          >
            {responsaveis.map((uma) => (
              <option key={uma.id} value={uma.id}>
                {uma.nome}
              </option>
            ))}
          </CampoSelecao>
        </div>

        <CampoTextoLongo
          rotulo="Observações"
          name="observacoes"
          maxLength={2000}
          defaultValue={movimentacao.observacoes ?? ''}
          className="mt-3"
        />

        <RecusaDaApi erro={salvar.error} padrao="Não foi possível salvar a movimentação." />

        <div className="mt-4 flex justify-end gap-2">
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao tipo="submit" variante="primario" desabilitado={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar movimentação'}
          </Botao>
        </div>
      </form>

      {/*
        Fora do formulário de propósito: o envio é imediato e tem botão próprio,
        e ficar dentro faria o Enter do anexo disparar o salvamento do pedido.

        A vida escolhida acima entra como padrão — a inclusão de um dependente é
        justamente o caso em que a operadora cobra o documento dele.
      */}
      <div className="mt-4">
        <DocumentosDoContato
          contatoId={movimentacao.contato.id}
          permitirEnvio
          compacto
          titulo="Documentos da movimentação"
          teste="movimentacao-documentos"
          vinculo={{ categoria: 'MOVIMENTACAO', movimentacaoId: movimentacao.id }}
          vidas={beneficiarios.data ?? []}
          {...(beneficiarioId ? { vidaPadrao: beneficiarioId } : {})}
        />
      </div>
    </Dialogo>
  );
}

/**
 * O prazo só entra no corpo quando tem valor.
 *
 * O contrato aceita trocar o prazo e não aceita apagá-lo — `z.iso.date()`
 * sem `nullish`. Mandar string vazia faria a API recusar a gravação inteira
 * de uma movimentação que nasceu sem prazo, e ela existe: o POST aceita
 * abrir sem.
 */
function prazoInformado(campos: FormData): { prazo?: string } {
  const prazo = String(campos.get('prazo') ?? '').trim();
  return prazo ? { prazo } : {};
}

function emDias(dias: number): string {
  const alvo = new Date();
  alvo.setDate(alvo.getDate() + dias);
  return alvo.toISOString().slice(0, 10);
}
